import { Component, computed, input, output } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { NotificationDto, NotificationId, NotificationType } from '@workspace/data-types';

@Component({
  selector: 'aura-notifications-dropdown',
  imports: [MatIcon, MatIconButton, MatButton, MatMenu, MatMenuTrigger],
  templateUrl: './notifications-dropdown.component.html',
})
export class AuraNotificationsDropdownComponent {
  notifications = input.required<NotificationDto[]>();
  unreadCount = input.required<number>();

  private readonly stableNow = computed(() => {
    this.notifications();
    return new Date();
  });

  unreadNotifications = computed(() => {
    const now = this.stableNow();
    return this.notifications()
      .filter((n) => !n.read)
      .map((n) => ({
        ...n,
        formattedTime: this.formatTimeWithNow(n.createdAt, now),
      }));
  });

  markAsRead = output<NotificationId>();
  markAllAsRead = output<void>();
  viewAll = output<void>();

  onNotificationClick(notification: NotificationDto): void {
    if (!notification.read) {
      this.markAsRead.emit(notification._id);
    }
  }

  getIconName(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'circle-check';
      case NotificationType.WARNING:
        return 'triangle-alert';
      case NotificationType.ERROR:
        return 'circle-x';
      case NotificationType.SYSTEM:
        return 'settings';
      case NotificationType.INFO:
      default:
        return 'info';
    }
  }

  getIconBgClass(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'bg-green-2 text-green-9';
      case NotificationType.WARNING:
        return 'bg-amber-2 text-amber-9';
      case NotificationType.ERROR:
        return 'bg-red-2 text-red-9';
      case NotificationType.SYSTEM:
        return 'bg-gray-2 text-gray-9';
      case NotificationType.INFO:
      default:
        return 'bg-blue-2 text-blue-9';
    }
  }

  formatTime(date: Date | string): string {
    if (!date) return '';
    return this.formatTimeWithNow(date, new Date());
  }

  formatTimeWithNow(date: Date | string, now: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays} j`;
  }
}
